import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Minus, Plus, X, Search, User } from "lucide-react";
import { calculateBookingPrice } from "@/lib/pricing";
import type { PricingRule, SpecialPriceDate } from "@/lib/pricing";
import { formatPrice } from "@/utils/formatPrice";
import type { BookingSheetFormProps } from "./types";

function Stepper({ value, onChange, min = 1, max = 99, label }: { value: number; onChange: (v: number) => void; min?: number; max?: number; label?: string }) {
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-sm text-muted-foreground mr-1">{label}</span>}
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>
        <Minus className="h-3 w-3" />
      </Button>
      <span className="w-8 text-center text-sm font-medium">{value}</span>
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  );
}

type HallDiscountType = "none" | "percent" | "fixed" | "free";

export interface RecurringConfig {
  enabled: boolean;
  days: number[];
  weeks: number;
}

interface PlayerSlotProps {
  index: number;
  playerId: string | null;
  playerName: string | null;
  isOrganizer: boolean;
  onSelect: (userId: string, name: string) => void;
  onRemove: () => void;
}

function PlayerSlot({ index, playerId, playerName, isOrganizer, onSelect, onRemove }: PlayerSlotProps) {
  const { t } = useTranslation("admin");
  const [search, setSearch] = useState("");

  const { data: results = [] } = useQuery({
    queryKey: ["activity-player-search", search],
    queryFn: async () => {
      const { data } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name")
        .ilike("full_name", `%${search}%`)
        .limit(6);
      return data ?? [];
    },
    enabled: search.length >= 2,
  });

  if (playerId && playerName) {
    return (
      <div className="flex items-center gap-2 h-9">
        <span className="text-xs text-muted-foreground w-14 shrink-0">{t("bookingSheet.slot", { number: index + 1 })}</span>
        <Badge variant="secondary" className="gap-1 text-xs font-normal h-7">
          <User className="h-3 w-3" />
          {playerName}
          {isOrganizer && (
            <Badge variant="default" className="text-[9px] h-4 px-1 ml-1">{t("bookingSheet.organizer")}</Badge>
          )}
          <button type="button" onClick={onRemove} className="ml-1 hover:text-destructive">
            <X className="h-3 w-3" />
          </button>
        </Badge>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 h-9 relative">
      <span className="text-xs text-muted-foreground w-14 shrink-0">{t("bookingSheet.slot", { number: index + 1 })}</span>
      {search.length > 0 ? (
        <div className="relative flex-1">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 text-xs pl-7 pr-2"
              placeholder={t("bookingSheet.searchPlayer")}
              autoFocus
            />
          </div>
          {results.length > 0 && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border border-border rounded-md shadow-md max-h-40 overflow-y-auto">
              {results.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    onSelect(p.id, p.full_name ?? t("bookingSheet.unknown"));
                    setSearch("");
                  }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors"
                >
                  {p.full_name ?? t("bookingSheet.unknown")}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setSearch(" ")}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {t("bookingSheet.leaveOpen")}
        </button>
      )}
    </div>
  );
}


interface BookingSheetOpenMatchFormProps extends BookingSheetFormProps {
  pricingRules: PricingRule[];
  hallId: string | null;
  startTime: Date | null;
  endTime: Date | null;
  courtId: string | null;
  specialDates?: SpecialPriceDate[];
  hallDiscountType: HallDiscountType;
  hallDiscountValue: number;
  organizerFee: number;
  onHallDiscountTypeChange: (v: HallDiscountType) => void;
  onHallDiscountValueChange: (v: number) => void;
  onOrganizerFeeChange: (v: number) => void;
  selectedPlayers: string[];
  playerNames: Record<string, string>;
  onPlayersChange: (players: string[], names: Record<string, string>) => void;
  recurring: RecurringConfig;
  onRecurringChange: (r: RecurringConfig) => void;
}

export default function BookingSheetOpenMatchForm({
  formState,
  onFieldChange,
  pricingRules,
  hallId,
  startTime,
  endTime,
  courtId: _courtId,
  specialDates: _specialDates = [],
  hallDiscountType,
  hallDiscountValue,
  organizerFee,
  onHallDiscountTypeChange,
  onHallDiscountValueChange,
  onOrganizerFeeChange,
  selectedPlayers,
  playerNames,
  onPlayersChange,
  recurring,
  onRecurringChange,
}: BookingSheetOpenMatchFormProps) {
  const { t } = useTranslation("admin");
  const DAY_LABELS = [t("bookingSheet.dayMonLong"), t("bookingSheet.dayTueLong"), t("bookingSheet.dayWedLong"), t("bookingSheet.dayThuLong"), t("bookingSheet.dayFriLong"), t("bookingSheet.daySatLong"), t("bookingSheet.daySunLong")];
  const {
    matchType,
    minLevel,
    maxLevel,
    requireLevel,
    autoCancel,
    autoCancelHours,
    autoCancelMinPlayers,
  } = formState;

  // Auto-calculate court cost from pricing rules
  const courtCost = useMemo(() => {
    if (!pricingRules.length || !hallId || !startTime || !endTime) return null;
    const durMin = (endTime.getTime() - startTime.getTime()) / 60000;
    if (durMin <= 0) return null;
    const result = calculateBookingPrice(pricingRules, hallId, startTime, durMin, "standard");
    return result.total > 0 ? result.total : null;
  }, [pricingRules, hallId, startTime, endTime]);

  // Effective court cost after hall discount
  const discountedCourtCost = useMemo(() => {
    if (courtCost == null) return null;
    switch (hallDiscountType) {
      case "free":
        return 0;
      case "percent":
        return Math.round(courtCost * (1 - hallDiscountValue / 100));
      case "fixed":
        return Math.max(0, courtCost - hallDiscountValue);
      default:
        return courtCost;
    }
  }, [courtCost, hallDiscountType, hallDiscountValue]);

  const maxPlayers = (matchType as string) === "single" ? 2 : 4;
  const perPerson = discountedCourtCost != null ? Math.round(discountedCourtCost / maxPlayers) : null;
  const totalPerPerson = perPerson != null ? perPerson + organizerFee : null;

  const filledCount = selectedPlayers.length;
  const openCount = maxPlayers - filledCount;

  // Recurring preview text
  const recurringPreview = useMemo(() => {
    if (!recurring.enabled || recurring.days.length === 0) return null;
    const dayNames = recurring.days
      .sort((a, b) => a - b)
      .map((d) => DAY_LABELS[d - 1])
      .join("+");
    const totalMatches = recurring.days.length * recurring.weeks;
    return t("bookingSheet.recurringPreview", { total: totalMatches, days: dayNames.toLowerCase(), weeks: recurring.weeks });
  }, [recurring]);

  return (
    <div className="space-y-3 border-t border-border pt-3">
      <div>
        <Label className="text-xs">{t("bookingSheet.matchType")}</Label>
        <Select value={matchType as string} onValueChange={(v) => onFieldChange("matchType", v)}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="double">{t("bookingSheet.doubles")}</SelectItem>
            <SelectItem value="single">{t("bookingSheet.singles")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">{t("bookingSheet.levelFrom")}</Label>
          <Stepper value={minLevel as number} onChange={(v) => onFieldChange("minLevel", v)} min={1} max={10} />
        </div>
        <div>
          <Label className="text-xs">{t("bookingSheet.levelTo")}</Label>
          <Stepper value={maxLevel as number} onChange={(v) => onFieldChange("maxLevel", v)} min={1} max={10} />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-xs">{t("bookingSheet.requireLevel")}</Label>
        <Switch checked={requireLevel as boolean} onCheckedChange={(v) => onFieldChange("requireLevel", v)} />
      </div>

      {/* Player slots section */}
      <div className="space-y-2 border-t border-border pt-3">
        <p className="text-sm font-medium">{t("bookingSheet.players")}</p>
        {Array.from({ length: maxPlayers }, (_, i) => (
          <PlayerSlot
            key={i}
            index={i}
            playerId={selectedPlayers[i] ?? null}
            playerName={selectedPlayers[i] ? (playerNames[selectedPlayers[i]] ?? null) : null}
            isOrganizer={i === 0 && !!selectedPlayers[0]}
            onSelect={(userId, name) => {
              const newPlayers = [...selectedPlayers];
              const newNames = { ...playerNames };
              // Fill next available slot or replace this one
              if (i < newPlayers.length) {
                newPlayers[i] = userId;
              } else {
                newPlayers.push(userId);
              }
              newNames[userId] = name;
              onPlayersChange(newPlayers, newNames);
            }}
            onRemove={() => {
              const newPlayers = selectedPlayers.filter((_, idx) => idx !== i);
              onPlayersChange(newPlayers, playerNames);
            }}
          />
        ))}
        <p className="text-xs text-muted-foreground">
          {t("bookingSheet.slotsFilled", { filled: filledCount, total: maxPlayers, open: openCount })}
        </p>
      </div>

      {/* Pricing section */}
      <div className="space-y-3 border-t border-border pt-3">
        <p className="text-sm font-medium">{t("bookingSheet.pricing")}</p>

        {courtCost != null && (
          <div className="text-xs text-muted-foreground">
            {t("bookingSheet.courtRentalStandard")}: <span className="font-medium">{formatPrice(courtCost)}</span>
          </div>
        )}

        {/* Hall discount */}
        <div className="space-y-2">
          <Label className="text-xs">{t("bookingSheet.hallDiscount")}</Label>
          <RadioGroup
            value={hallDiscountType}
            onValueChange={(v) => onHallDiscountTypeChange(v as HallDiscountType)}
            className="grid grid-cols-2 gap-1.5"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="none" id="hd-none" />
              <Label htmlFor="hd-none" className="text-xs font-normal cursor-pointer">{t("bookingSheet.noDiscount")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="percent" id="hd-percent" />
              <Label htmlFor="hd-percent" className="text-xs font-normal cursor-pointer">{t("bookingSheet.percentage")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="fixed" id="hd-fixed" />
              <Label htmlFor="hd-fixed" className="text-xs font-normal cursor-pointer">{t("bookingSheet.fixedAmount")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="free" id="hd-free" />
              <Label htmlFor="hd-free" className="text-xs font-normal cursor-pointer">{t("bookingSheet.freeCourt")}</Label>
            </div>
          </RadioGroup>

          {(hallDiscountType === "percent" || hallDiscountType === "fixed") && (
            <div className="flex items-center gap-2">
              <NumberInput
                min={0}
                max={hallDiscountType === "percent" ? 100 : undefined}
                value={hallDiscountValue}
                onValueChange={(v) => onHallDiscountValueChange(v)}
                className="h-8 w-24 text-sm"
              />
              <span className="text-xs text-muted-foreground">
                {hallDiscountType === "percent" ? "%" : "kr"}
              </span>
            </div>
          )}
        </div>

        {/* Organizer fee */}
        <div>
          <Label className="text-xs">{t("bookingSheet.organizerFeePerPerson")}</Label>
          <NumberInput
            min={0}
            value={organizerFee || ""}
            onValueChange={(v) => onOrganizerFeeChange(v)}
            className="h-9 text-sm w-32"
            placeholder="0"
          />
        </div>

        {/* Live example calculation */}
        {courtCost != null && (
          <Card className="bg-muted/50">
            <CardContent className="p-3 space-y-1 text-xs">
              <p className="font-medium text-sm">{t("bookingSheet.estimatedPricePerPerson")}</p>
              <div className="space-y-0.5 text-muted-foreground">
                <p>{t("bookingSheet.courtRental")}: {formatPrice(courtCost)}{discountedCourtCost !== courtCost && discountedCourtCost != null ? ` → ${formatPrice(discountedCourtCost)} (${t("bookingSheet.afterDiscount")})` : ""}</p>
                <p>{t("bookingSheet.splitBetween", { count: maxPlayers })}: {perPerson != null ? formatPrice(perPerson) : "–"}</p>
                {organizerFee > 0 && <p>+ {t("bookingSheet.organizerFee")}: {formatPrice(organizerFee)}</p>}
              </div>
              <p className="font-semibold text-sm pt-1 border-t border-border mt-1">
                {t("bookingSheet.total")}: {totalPerPerson != null ? formatPrice(totalPerPerson) : "–"} / {t("bookingSheet.person")}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recurring section */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{t("bookingSheet.recurrence")}</p>
          <Switch
            checked={recurring.enabled}
            onCheckedChange={(v) => onRecurringChange({ ...recurring, enabled: v })}
          />
        </div>

        {recurring.enabled && (
          <div className="space-y-3 pl-1">
            <div>
              <Label className="text-xs mb-1.5 block">{t("bookingSheet.every")}</Label>
              <div className="flex flex-wrap gap-1.5">
                {DAY_LABELS.map((label, i) => {
                  const dayNum = i + 1; // 1=Mon, 7=Sun
                  const isSelected = recurring.days.includes(dayNum);
                  return (
                    <button
                      key={dayNum}
                      type="button"
                      onClick={() => {
                        const newDays = isSelected
                          ? recurring.days.filter((d) => d !== dayNum)
                          : [...recurring.days, dayNum];
                        onRecurringChange({ ...recurring, days: newDays });
                      }}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-border hover:border-primary/40"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs">{t("bookingSheet.numberOfWeeks")}</Label>
              <Stepper
                value={recurring.weeks}
                onChange={(v) => onRecurringChange({ ...recurring, weeks: v })}
                min={1}
                max={52}
              />
            </div>

            {recurringPreview && (
              <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
                {recurringPreview}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-xs">{t("bookingSheet.autoCancel")}</Label>
        <Switch checked={autoCancel as boolean} onCheckedChange={(v) => onFieldChange("autoCancel", v)} />
      </div>
      {autoCancel && (
        <div className="grid grid-cols-2 gap-3 pl-4">
          <div>
            <Label className="text-xs">{t("bookingSheet.hoursBefore")}</Label>
            <Stepper value={autoCancelHours as number} onChange={(v) => onFieldChange("autoCancelHours", v)} min={1} max={48} />
          </div>
          <div>
            <Label className="text-xs">{t("bookingSheet.minPlayers")}</Label>
            <Stepper value={autoCancelMinPlayers as number} onChange={(v) => onFieldChange("autoCancelMinPlayers", v)} min={2} max={8} />
          </div>
        </div>
      )}
    </div>
  );
}
